import React, { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Calculator, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, SectionLabel, PageHero, PrimaryCTA } from "@/components/ui-kit";
import { FinalCTA } from "@/components/Sections";
import { CALC_META, CALC_BY_SLUG, calcPath } from "@/components/calculators/meta";
import { CALCS } from "@/components/calculators/Calculators";
import { ARTICLES } from "@/content/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { CONFIG } from "@/config";

const KEYS = Object.keys(CALC_META);

const CalcNav = ({ active }) => (
  <div className="flex flex-wrap gap-2" data-testid="calc-tabs">
    {KEYS.map((k) => (
      <Link key={k} to={calcPath(k)} data-testid={`calc-tab-${k}`} aria-current={active === k ? "page" : undefined}
        className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${active === k ? "bg-navy text-white" : "border border-navy/10 bg-lightbg text-navy/75 hover:border-navy/40"}`}>
        {CALC_META[k].short}
      </Link>
    ))}
  </div>
);

const CalcGrid = ({ active }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {KEYS.filter((k) => k !== active).map((k, i) => (
      <Reveal key={k} delay={(i % 3) * 0.06}>
        <Link to={calcPath(k)} data-testid={`calc-card-${k}`} className="block h-full rounded-2xl border border-navy/[0.08] bg-white p-6 transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-hover">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/5 text-royal"><Calculator className="h-5 w-5" /></span>
          <h3 className="mt-4 font-display text-lg font-bold text-navy">{CALC_META[k].title}</h3>
          <p className="mt-1.5 text-sm text-ink/60">{CALC_META[k].line}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-royal">Open <ArrowRight className="h-4 w-4" /></span>
        </Link>
      </Reveal>
    ))}
  </div>
);

// Hub: /calculators
export const CalculatorsHub = () => {
  usePageMeta({
    title: "Free Financial Calculators — SIP, Lumpsum, Goal, EMI, Retirement & Term Insurance",
    description: "Six free calculators from Auxieron: SIP returns, lumpsum growth, goal SIP planner, loan EMI, retirement corpus and term insurance cover. Instant results, no sign-up.",
    path: "/calculators",
    jsonLd: {
      "@context": "https://schema.org", "@type": "CollectionPage", "name": "Auxieron Financial Calculators", "url": `${CONFIG.website}/calculators`,
      "hasPart": KEYS.map((k) => ({ "@type": "SoftwareApplication", "name": CALC_META[k].h1, "url": `${CONFIG.website}${calcPath(k)}`, "applicationCategory": "FinanceApplication", "operatingSystem": "Web", "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" } })),
    },
  });
  return (
    <>
      <PageHero label="Calculators" title="Put numbers to" highlight="your plans." lead="Six simple, free tools to turn vague goals into concrete figures. Pick a calculator, adjust the sliders, see the result instantly — then talk to us about what it means for you." testId="calculators-label" />
      <section className="bg-white py-14 sm:py-20"><div className="mx-auto max-w-7xl px-5 sm:px-8"><CalcGrid /></div></section>
      <FinalCTA />
    </>
  );
};

// Detail: /calculators/:slug
export default function CalculatorPage() {
  const { slug } = useParams();
  const key = CALC_BY_SLUG[slug];
  const meta = key ? CALC_META[key] : null;

  const jsonLd = useMemo(() => meta && {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "SoftwareApplication", "name": meta.h1, "url": `${CONFIG.website}${calcPath(key)}`, "description": meta.description, "applicationCategory": "FinanceApplication", "operatingSystem": "Web", "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" }, "publisher": { "@type": "Organization", "name": CONFIG.legalName, "url": CONFIG.website } },
      { "@type": "BreadcrumbList", "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": CONFIG.website },
        { "@type": "ListItem", "position": 2, "name": "Calculators", "item": `${CONFIG.website}/calculators` },
        { "@type": "ListItem", "position": 3, "name": meta.h1, "item": `${CONFIG.website}${calcPath(key)}` },
      ] },
      { "@type": "FAQPage", "mainEntity": meta.faqs.map(([q, a]) => ({ "@type": "Question", "name": q, "acceptedAnswer": { "@type": "Answer", "text": a } })) },
    ],
  }, [meta, key]);

  usePageMeta({ title: meta ? meta.seoTitle : "Calculator", description: meta ? meta.description : "", path: meta ? calcPath(key) : "/calculators", jsonLd, noSuffix: true });

  if (!meta) return <Navigate to="/calculators" replace />;
  const Active = CALCS[key];
  const related = ARTICLES.filter((a) => a.calculator === key).slice(0, 3);

  return (
    <>
      <section className="relative overflow-hidden bg-lightbg pt-28 sm:pt-32 lg:pt-36">
        <div className="pointer-events-none absolute -right-40 -top-24 h-[480px] w-[480px] rounded-full bg-royal/[0.07] blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 pb-10 sm:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-navy/50" data-testid="calc-breadcrumb">
            <Link to="/" className="hover:text-navy">Home</Link><span>/</span><Link to="/calculators" className="hover:text-navy">Calculators</Link><span>/</span><span className="text-navy">{meta.h1}</span>
          </nav>
          <SectionLabel testId="calc-active-label">Free calculator</SectionLabel>
          <h1 data-testid="calc-h1" className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl">{meta.h1}</h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg">{meta.intro}</p>
          <div className="mt-8"><CalcNav active={key} /></div>
        </div>
      </section>

      <section id="calculator" className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-testid={`calc-panel-${key}`}>
          <h2 className="font-display text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{meta.line}</h2>
          <div className="mt-8"><Active /></div>
        </div>
      </section>

      <section className="bg-lightbg py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <SectionLabel testId="calc-how-label">How to use this calculator</SectionLabel>
            <ol className="mt-5 space-y-3">
              {meta.how.map((step, i) => (
                <li key={step} className="flex items-start gap-3 text-[15px] text-ink/75"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gold text-xs font-bold text-navy">{i + 1}</span> {step}</li>
              ))}
            </ol>
            <div className="mt-6 rounded-2xl border border-navy/[0.08] bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Formula used</p>
              <p data-testid="calc-formula" className="mt-2 font-mono text-sm leading-relaxed text-navy">{meta.formula}</p>
            </div>
            <h2 className="mt-10 font-display text-2xl font-bold text-navy">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-4 space-y-3" data-testid="calc-faq">
              {meta.faqs.map(([q, a], i) => (
                <AccordionItem key={q} value={`f-${i}`} className="overflow-hidden rounded-2xl border border-navy/[0.08] bg-white px-5">
                  <AccordionTrigger className="py-4 text-left font-display text-base font-bold text-navy hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="pb-4 text-[15px] leading-relaxed text-ink/65">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-navy p-7 text-white">
              <h3 className="font-display text-xl font-bold">Numbers are a start. A plan is better.</h3>
              <ul className="mt-4 space-y-2 text-sm text-white/75">
                {["Personalised to your income and goals", "Covers investments, protection, tax and borrowing", "No obligation, no product pushing"].map((t) => <li key={t} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {t}</li>)}
              </ul>
              <div className="mt-6 flex flex-col gap-2">
                <PrimaryCTA testId="calc-primary-cta" className="!px-5 !py-3 !text-sm">Get my personalised plan</PrimaryCTA>
                <Link to="/book" data-testid="calc-book-cta" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10">Book a free call</Link>
              </div>
            </div>
            {related.length > 0 && (
              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Read next</p>
                <div className="mt-3 space-y-3">
                  {related.map((a) => (
                    <Link key={a.slug} to={`/learn/${a.slug}`} data-testid={`calc-article-${a.slug}`} className="group flex gap-4 rounded-2xl border border-navy/[0.08] bg-white p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-hover">
                      <img src={a.cover} alt={a.alt} loading="lazy" className="h-16 w-24 shrink-0 rounded-xl object-cover" />
                      <div><p className="text-[11px] font-semibold uppercase tracking-wide text-royal">{a.category}</p><h4 className="mt-1 font-display text-sm font-bold leading-snug text-navy group-hover:text-royal">{a.title}</h4></div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><SectionLabel testId="calc-all-label">More calculators</SectionLabel></Reveal>
          <div className="mt-8"><CalcGrid active={key} /></div>
          <Link to="/calculators" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-navy"><ArrowLeft className="h-4 w-4" /> All calculators</Link>
        </div>
      </section>
    </>
  );
}
